// pages/sop/SopEditor.jsx
import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import LinkIcon from "@mui/icons-material/Link";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

// ─── Design tokens (matched to FoldersList) ─────────────────────────────────
const TEAL = "#0c8a9b";
const TEAL_DARK = "#0a7a8a";
const TEAL_LIGHT = "#e6f6f8";
const FONT = "'DM Sans', 'Inter', sans-serif";

// ─── Keyframes (shared id so it only injects once) ──────────────────────────
const KF_ID = "sop-editor-kf";
if (typeof document !== "undefined" && !document.getElementById(KF_ID)) {
  const s = document.createElement("style");
  s.id = KF_ID;
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
    @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  `;
  document.head.appendChild(s);
}

// ─── Quill styling — fixed-height box, toolbar never moves, only the
//     text content scrolls inside (page itself doesn't grow) ──────────────
const StyledQuill = styled(Box)({
  display: "flex",
  flexDirection: "column",
  border: "1px solid #edf0f2",
  borderRadius: "12px",
  overflow: "hidden",
  height: 420, // fixed height for the whole editor box — toolbar stays put
  "& .quill": {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  "& .ql-toolbar.ql-snow": {
    border: "none",
    borderBottom: "1px solid #edf0f2",
    backgroundColor: "#fafbfc",
    flexShrink: 0, // toolbar height is fixed, never scrolls
  },
  "& .ql-container.ql-snow": {
    border: "none",
    fontFamily: FONT,
    flex: 1,
    overflow: "hidden", // ql-editor inside handles its own scroll
  },
  "& .ql-editor": {
    height: "100%",
    overflowY: "auto", // only the written content scrolls
    fontSize: "14px",
    lineHeight: 1.7,
    fontFamily: FONT,
  },
  "& .ql-snow.ql-toolbar button:hover, & .ql-snow .ql-toolbar button:hover": {
    color: TEAL,
  },
  "& .ql-snow.ql-toolbar button.ql-active .ql-stroke": { stroke: TEAL },
  "& .ql-snow.ql-toolbar button.ql-active .ql-fill": { fill: TEAL },
  "& .ql-snow.ql-toolbar button.ql-active": { color: TEAL },
});

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontFamily: FONT,
    fontSize: "0.9rem",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: TEAL },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: TEAL },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: TEAL },
};

// ─── Section wrapper card ───────────────────────────────────────────────────
const SectionCard = ({ title, action, children, delay = "0s" }) => (
  <Card
    sx={{
      borderRadius: "16px",
      border: "1px solid #edf0f2",
      boxShadow: "0 1px 2px rgba(16,24,40,.04)",
      mb: 2.5,
      animation: `fadeUp .45s ease ${delay} both`,
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.6,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: "0.95rem",
            color: "#111827",
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const SopEditor = () => {
  const { folderId, sopId } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useContext(AutoTaskAuthContext);
  const store_id = userInfo?.store_id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    content: "",
    file_attachments: [],
    link_attachments: [],
    status: "DRAFT",
  });

  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [tempFile, setTempFile] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkName, setLinkName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  useEffect(() => {
    if (sopId) {
      setLoading(true);
      axios
        .get(`${API_BASE}/api/sop-manager/sops/${sopId}`)
        .then((res) => setFormData(res.data.data))
        .catch((err) => setError("Failed to load SOP"))
        .finally(() => setLoading(false));
    }
  }, [sopId]);

  const handleChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  // Upload file to backend and get blob URL
  const uploadAndAddFile = async (file) => {
    setUploadingFile(true);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    try {
      const response = await axios.post(
        `${API_BASE}/api/sop-manager/upload`,
        uploadFormData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      if (response.data.success) {
        const fileMetadata = response.data.data;
        setFormData((prev) => ({
          ...prev,
          file_attachments: [...prev.file_attachments, fileMetadata],
        }));
      } else {
        alert(response.data.statusMessage || "Upload failed");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to upload file");
    } finally {
      setUploadingFile(false);
      setFileDialogOpen(false);
      setTempFile(null);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setTempFile(file);
    setFileDialogOpen(true);
  };

  const confirmAddFile = async () => {
    if (!tempFile) return;
    await uploadAndAddFile(tempFile);
  };

  const addLink = () => {
    if (!linkName || !linkUrl) return alert("Please fill both name and URL");
    setFormData((prev) => ({
      ...prev,
      link_attachments: [
        ...prev.link_attachments,
        { name: linkName, url: linkUrl },
      ],
    }));
    setLinkDialogOpen(false);
    setLinkName("");
    setLinkUrl("");
  };

  const removeFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      file_attachments: prev.file_attachments.filter((_, i) => i !== index),
    }));
  };

  const removeLink = (index) => {
    setFormData((prev) => ({
      ...prev,
      link_attachments: prev.link_attachments.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return alert("Title is required");
    setSaving(true);
    setError("");
    try {
      const payload = {
        store_id,
        folder_id: parseInt(folderId),
        title: formData.title,
        description: formData.description,
        content: formData.content,
        file_attachments: formData.file_attachments,
        link_attachments: formData.link_attachments,
        status: formData.status,
      };
      if (sopId) {
        await axios.put(`${API_BASE}/api/sop-manager/sops/${sopId}`, payload);
      } else {
        await axios.post(`${API_BASE}/api/sop-manager/sops`, payload);
      }
      navigate(`/ats/sop/folder/${folderId}`);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.statusMessage || "Failed to save SOP");
    } finally {
      setSaving(false);
    }
  };

  const statusStyles = {
    DRAFT: { bg: "#f1f3f5", color: "#64748b" },
    PUBLISHED: { bg: TEAL_LIGHT, color: TEAL },
    ARCHIVED: { bg: "#fef3e2", color: "#e07b00" },
  };

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress sx={{ color: TEAL }} />
      </Box>
    );

  if (error)
    return (
      <Box sx={{ maxWidth: 1000, mx: "auto", p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: "12px", fontFamily: FONT }}>
          {error}
        </Alert>
      </Box>
    );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#f8f9fa",
        fontFamily: FONT,
      }}
    >
      {/* ── Sticky Header (title input, status, save button — always reachable) ── */}
      <Box
        sx={{
          zIndex: 20,
          background: "rgba(248,249,250,.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid #edf0f2",
          px: { xs: 2, sm: 30 },
          py: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: 1000,
            mx: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Tooltip title="Back">
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                bgcolor: "#fff",
                border: "1px solid #edf0f2",
                borderRadius: "10px",
                "&:hover": { borderColor: TEAL, color: TEAL },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: "1.05rem",
                color: "#1a1d23",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {formData.title || (sopId ? "Edit SOP" : "New SOP")}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "0.75rem",
                color: "#868e96",
              }}
            >
              {sopId ? "Editing existing document" : "Creating new document"}
            </Typography>
          </Box>

          <TextField
            select
            size="small"
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            sx={{
              minWidth: 130,
              display: { xs: "none", sm: "flex" },
              ...inputSx,
            }}
          >
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="PUBLISHED">Published</MenuItem>
            <MenuItem value="ARCHIVED">Archived</MenuItem>
          </TextField>

          <Chip
            label={formData.status}
            size="small"
            sx={{
              display: { xs: "flex", sm: "none" },
              bgcolor: statusStyles[formData.status]?.bg,
              color: statusStyles[formData.status]?.color,
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: "0.7rem",
            }}
          />

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              background: TEAL,
              borderRadius: "10px",
              px: 2.4,
              color: "#fff",
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: "0.85rem",
              textTransform: "none",
              boxShadow: "none",
              whiteSpace: "nowrap",
              "&:hover": { background: TEAL_DARK, boxShadow: "none" },
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </Box>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <Box sx={{ maxWidth: 1000, mx: "auto", p: { xs: 2, sm: 3.5 } }}>
        <SectionCard title="Details" delay="0s">
          <TextField
            fullWidth
            label="Title"
            value={formData.title}
            onChange={(e) => handleChange("title", e.target.value)}
            required
            sx={{ mb: 2, ...inputSx }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            sx={inputSx}
          />
          {/* Status select for mobile, where the header chip is read-only */}
          <TextField
            select
            label="Status"
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            sx={{
              mt: 2,
              minWidth: 160,
              display: { xs: "flex", sm: "none" },
              ...inputSx,
            }}
          >
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="PUBLISHED">Published</MenuItem>
            <MenuItem value="ARCHIVED">Archived</MenuItem>
          </TextField>
        </SectionCard>

        <SectionCard
          title="File Attachments"
          delay=".05s"
          action={
            <Button
              variant="outlined"
              component="label"
              size="small"
              startIcon={<AttachFileIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: "8px",
                fontFamily: FONT,
                fontWeight: 600,
                fontSize: "0.78rem",
                textTransform: "none",
                borderColor: "#dfe3e6",
                color: "#495057",
                "&:hover": {
                  borderColor: TEAL,
                  color: TEAL,
                  bgcolor: TEAL_LIGHT,
                },
              }}
            >
              Upload File
              <input type="file" hidden onChange={handleFileSelect} />
            </Button>
          }
        >
          {formData.file_attachments.length === 0 ? (
            <Typography
              sx={{ fontFamily: FONT, fontSize: "0.82rem", color: "#adb5bd" }}
            >
              No files attached yet.
            </Typography>
          ) : (
            <Grid container spacing={1}>
              {formData.file_attachments.map((file, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      p: 1.2,
                      borderRadius: "10px",
                      border: "1px solid #edf0f2",
                      bgcolor: "#fafbfc",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          bgcolor: TEAL_LIGHT,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <InsertDriveFileOutlinedIcon
                          sx={{ fontSize: 17, color: TEAL }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: FONT,
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: "#1a1d23",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {file.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: FONT,
                            fontSize: "0.72rem",
                            color: "#868e96",
                          }}
                        >
                          {(file.size / 1024).toFixed(0)} KB
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeFile(idx)}
                      sx={{ color: "#adb5bd", "&:hover": { color: "#e03131" } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </SectionCard>

        <SectionCard
          title="Link Attachments"
          delay=".1s"
          action={
            <Button
              variant="outlined"
              size="small"
              startIcon={<LinkIcon sx={{ fontSize: 16 }} />}
              onClick={() => setLinkDialogOpen(true)}
              sx={{
                borderRadius: "8px",
                fontFamily: FONT,
                fontWeight: 600,
                fontSize: "0.78rem",
                textTransform: "none",
                borderColor: "#dfe3e6",
                color: "#495057",
                "&:hover": {
                  borderColor: TEAL,
                  color: TEAL,
                  bgcolor: TEAL_LIGHT,
                },
              }}
            >
              Add Link
            </Button>
          }
        >
          {formData.link_attachments.length === 0 ? (
            <Typography
              sx={{ fontFamily: FONT, fontSize: "0.82rem", color: "#adb5bd" }}
            >
              No links added yet.
            </Typography>
          ) : (
            <Grid container spacing={1}>
              {formData.link_attachments.map((link, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      p: 1.2,
                      borderRadius: "10px",
                      border: "1px solid #edf0f2",
                      bgcolor: "#fafbfc",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          bgcolor: "#edf2ff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <LinkIcon sx={{ fontSize: 17, color: "#3b5bdb" }} />
                      </Box>
                      <Typography
                        component="a"
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: FONT,
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          color: TEAL,
                          textDecoration: "none",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {link.name}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeLink(idx)}
                      sx={{ color: "#adb5bd", "&:hover": { color: "#e03131" } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </SectionCard>

        <SectionCard title="Content" delay=".15s">
          <StyledQuill>
            <ReactQuill
              theme="snow"
              value={formData.content}
              onChange={(val) => handleChange("content", val)}
              modules={quillModules}
              placeholder="Write the SOP content here..."
            />
          </StyledQuill>
        </SectionCard>
      </Box>

      {/* ── File confirmation dialog with upload loading ─────────────── */}
      <Dialog
        open={fileDialogOpen}
        onClose={() => !uploadingFile && setFileDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: "16px", fontFamily: FONT, minWidth: 320 },
        }}
      >
        <DialogTitle
          sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "1rem" }}
        >
          Add File
        </DialogTitle>
        <DialogContent sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: "0.88rem" }}>
            Add file: {tempFile?.name}
          </Typography>
          {uploadingFile && <CircularProgress size={20} sx={{ color: TEAL }} />}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={() => setFileDialogOpen(false)}
            disabled={uploadingFile}
            sx={{
              fontFamily: FONT,
              fontWeight: 500,
              color: "#868e96",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmAddFile}
            variant="contained"
            disabled={uploadingFile}
            sx={{
              background: TEAL,
              borderRadius: "8px",
              fontFamily: FONT,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: TEAL_DARK, boxShadow: "none" },
            }}
          >
            {uploadingFile ? "Uploading..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Link dialog ───────────────────────────────────────────────── */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", fontFamily: FONT } }}
      >
        <DialogTitle
          sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "1rem" }}
        >
          Add Link
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Link Name"
            fullWidth
            value={linkName}
            onChange={(e) => setLinkName(e.target.value)}
            sx={inputSx}
          />
          <TextField
            margin="dense"
            label="URL"
            fullWidth
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://..."
            sx={inputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={() => setLinkDialogOpen(false)}
            sx={{
              fontFamily: FONT,
              fontWeight: 500,
              color: "#868e96",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={addLink}
            variant="contained"
            sx={{
              background: TEAL,
              borderRadius: "8px",
              fontFamily: FONT,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: TEAL_DARK, boxShadow: "none" },
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SopEditor;
